<template>
  <div>
    <v-dialog v-model="optimalZonesInfoDialog" max-width="50%">
      <v-card>
        <v-card-title class="headline">{{ $t('components.SaturationCurves.aboutOptimalZones') }}</v-card-title>
        <v-card-text>
          <v-icon class="pb-1" dense color="darkgrey">mdi-square-rounded-outline</v-icon><b>{{ $t('components.SaturationCurves.zone1') }}:</b> {{ $t('components.SaturationCurves.zone1Desc') }}<br /><br />
          <v-icon class="pb-1" dense color="rgba(213, 244, 254)">mdi-square-rounded</v-icon><b>{{ $t('components.SaturationCurves.zone2') }}:</b> {{ $t('components.SaturationCurves.zone2Desc') }}<br /><br />
          <v-icon class="pb-1" dense color="rgba(235, 247, 225)">mdi-square-rounded</v-icon><b>{{ $t('components.SaturationCurves.zone3') }}:</b> {{ $t('components.SaturationCurves.zone3Desc') }}<br /><br />
          <v-icon class="pb-1" dense color="rgba(255, 243, 215)">mdi-square-rounded</v-icon><b>{{ $t('components.SaturationCurves.zone4') }}:</b> {{ $t('components.SaturationCurves.zone4Desc') }}<br /><br />
          <v-icon class="pb-1" dense color="rgba(255, 216, 219)">mdi-square-rounded</v-icon><b>{{ $t('components.SaturationCurves.zone5') }}:</b> {{ $t('components.SaturationCurves.zone5Desc') }}<br /><br />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="optimalZonesInfoDialog = false">{{ $t('components.SaturationCurves.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="marginalRoasInfoDialog" max-width="50%">
      <v-card>
        <v-card-title class="headline">{{ $t('components.SaturationCurves.aboutMarginalRoas') }}</v-card-title>
        <v-card-text>
          {{ $t('components.SaturationCurves.marginalRoasDesc') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="marginalRoasInfoDialog = false">{{ $t('components.SaturationCurves.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="alternativeUnitDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t('components.SaturationCurves.averageCpi') }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <p class="text-subtitle-1 mb-4">{{ $t('components.SaturationCurves.enterCpiInstructions') }}</p>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-for="([channel, color]) in sortedChannels"
                :key="`alt_unit_${channel}`"
                cols="12"
                md="6"
                class="py-2"
              >
              <v-text-field
                :label="channel"
                :value="tempAlternativeUnitValues[channel]"
                @change="(value) => filterNumericInput(value, channel)"
                type="number"
                outlined
                dense
                hide-details="auto"
                class="channel-input no-spinner"
              >
              <template v-slot:prepend-inner>
                <v-icon :color="color" small class="mr-2">mdi-circle</v-icon>
              </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
          <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
            <v-btn
              text
              @click="alternativeUnitDialog = false"
              class="mr-2"
            >
              {{ $t('components.SaturationCurves.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              @click="saveAlternativeUnits"
              elevated
            >
              {{ $t('components.SaturationCurves.save') }}
            </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <text-box class="text-box-saturation" :label="$t('components.SaturationCurves.saturationCurves')" icon="chart-bell-curve-cumulative" :hideTitle="hideTitle">
      <template v-slot:info>
        <v-select v-if="hasSaturationGroups"
          v-model="selectedSaturationGroup"
          :items="saturationGroupItems"
          dense hide-details dark solo flat
          background-color="grey darken-4"
          class="mr-2 saturation-group-select"
          style="max-width: 240px"></v-select>
      </template>
      <v-container fluid>
        <div class="d-flex align-end" ref="saturationTabsRow">
          <v-tabs v-model="tabSaturation" ref="saturationTabs">
            <v-tab key="generalView" v-show="!client.enableImpressionsModel">{{ $t('components.SaturationCurves.generalView') }}</v-tab>
            <v-tab key="optimalZones" v-show="showOptimalZones && !client.enableImpressionsModel">{{ $t('components.SaturationCurves.optimalZones') }}</v-tab>
            <v-tab key="marginalRoas" v-if="showMarginalRoas && !client.enableImpressionsModel">{{ $t('components.SaturationCurves.marginalRoas') }}</v-tab>
          </v-tabs>
          <v-select v-if="showPeriodCollapse && !stackDatapointsSelect"
            v-model="selectedPeriodCollapse"
            :items="periodCollapseItems"
            :label="$t(datapointsLabelKey)"
            dense hide-details
            class="ml-4 flex-grow-0 datapoints-period-select"
            :style="`min-width: ${datapointsSelectWidth}px; max-width: ${datapointsSelectWidth}px`"></v-select>
        </div>
        <div v-if="showPeriodCollapse && stackDatapointsSelect" class="d-flex justify-end pt-3">
          <v-select
            v-model="selectedPeriodCollapse"
            :items="periodCollapseItems"
            :label="$t(datapointsLabelKey)"
            dense hide-details
            class="flex-grow-0 datapoints-period-select"
            :style="`width: ${datapointsSelectWidth}px; max-width: 100%`"></v-select>
        </div>
        <v-row v-if="client.enableImpressionsModel">
          <v-col cols="9"></v-col>
          <v-col cols="3">
            <div class="d-flex align-center">
              <v-select class="pt-5 pr-5 pl-5 flex-grow-1"
                v-model="selectedSaturationSpendUnit"
                :items="saturationSpendUnitOptions"
                :label="$t('components.SaturationCurves.spendUnit')">
              </v-select>
              <v-tooltip bottom v-if="selectedSaturationSpendUnit === 'MXN' && hasSaturationAlternativeValues">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="openEditCpiModal"
                    class="ml-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('components.SaturationCurves.editAverageCpi') }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
        <!-- Optimal Zones content for impressions model (outside of tabs) -->
        <div v-if="client.enableImpressionsModel">
          <v-row v-if="!showOptimalZonesMessage">
            <v-col cols="12">
              <div class="mt-8 mb-5 float-right mr-10">
                <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                    color="darkgrey">mdi-square-rounded-outline</v-icon> {{ $t('components.SaturationCurves.zone1') }}</span>
                <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                    color="rgba(213, 244, 254)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone2') }}</span>
                <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                    color="rgba(235, 247, 225)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone3') }}</span>
                <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                    color="rgba(255, 243, 215)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone4') }}</span>
                <span style="color: darkgrey; padding-right: 10px;"><v-icon class="pb-1" dense
                    color="rgba(255, 216, 219)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone5') }}</span>
                <span><v-icon class="pb-1" dense color="primary" small
                    @click.stop="optimalZonesInfoDialog = true">mdi-information-outline</v-icon></span>
              </div>
            </v-col>
          </v-row>
          <v-row v-if="!showOptimalZonesMessage">
            <v-col :cols="channelCols" v-for="[key, color] in channelEntries" v-bind:key="`optimalZone_${key}`">
              <span :style="{ textDecoration: 'underline', textDecorationColor: color }">{{ key }}</span>
              <channel-curves-chart-multi :data="saturationForKey(key)"
                :key="`optimalZoneChart_${key}_${selectedPeriodCollapse}`"
                :titleString="`${$t('components.SaturationCurves.optimalZones')} [${key}]`"
                :exportImageName="`${client.name} - Optimal Zones [${key}]`"
                :x-name="saturationXAxisName"
                :y-name="`${customKpiName} (${outputUnitName})`" icon="chart-bell-curve-cumulative"
                :export="true" :colors="channelColors" :show-diagonal="true" :show-data-zoom="true"
                :show-tooltips="true" :scale="true" :filter-mode="null" :hide-title="true"
                :period="chartPeriod" :show-quarters="chartShowQuarters"
                :hide-datapoints="!showSaturationDatapoints"
                :show-legend="false" elevation="elevation-0" />
            </v-col>
          </v-row>
          <v-row v-if="showOptimalZonesMessage">
            <v-col cols="12">
              <div class="text-center grey--text text--darken-1 py-8">
                {{ $t('components.SaturationCurves.noOptimalZonesData', { name: selectedGroupDisplayName }) }}
              </div>
            </v-col>
          </v-row>
        </div>
        <!--
          Tab bodies are plain v-show panels (NOT v-tabs-items / v-tab-item): Vuetify's
          v-tab-item destroys the inactive panel on switch, which would re-initialize the
          ECharts instances (the section collapsing + redrawing on every tab change).
          Each panel is mounted lazily the first time its tab is opened (bootedSaturationTabs)
          and then kept alive, so switching tabs only toggles `display` and charts persist.
        -->
        <div>
          <v-container fluid
            v-if="bootedSaturationTabs.includes(0) && !client.enableImpressionsModel"
            v-show="tabSaturation === 0 && !client.enableImpressionsModel">
            <v-row>
              <v-col cols="12">
                <div v-if="canExportCurves">
                  <v-btn x-small @click="calculateHillParameters(effectiveSaturationData)">{{ $t('components.SaturationCurves.exportCurves') }}</v-btn>
                </div>
                <channel-curves-chart-multi
                  :key="`satChart_${selectedPeriodCollapse}`"
                  :data="adjustedSaturationData" :titleString="$t('components.SaturationCurves.saturationCurves')"
                  :exportImageName="`${client.name} - Saturation Curves`"
                  :x-name="saturationXAxisName"
                  :y-name="`${customKpiName} (${outputUnitName})`"
                  icon="chart-bell-curve-cumulative" :export="true" :colors="channelColors"
                  :show-diagonal="true" :show-data-zoom="true"
                  :legend-orient="compact ? 'horizontal' : 'vertical'" :legend-bottom="compact"
                  :hide-datapoints="!showSaturationDatapoints" :show-tooltips="true" :scale="true"
                  :period="chartPeriod" :show-quarters="chartShowQuarters"
                  :filter-mode="null" :hide-title="true" elevation="elevation-0" />
              </v-col>
            </v-row>
          </v-container>
          <div
            v-if="bootedSaturationTabs.includes(1) || client.enableImpressionsModel"
            v-show="tabSaturation === 1 || client.enableImpressionsModel">
            <v-row v-if="!showOptimalZonesMessage">
              <v-col cols="12">
                <div class="mt-8 mb-5 float-right mr-10">
                  <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                      color="darkgrey">mdi-square-rounded-outline</v-icon> {{ $t('components.SaturationCurves.zone1') }}</span>
                  <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                      color="rgba(213, 244, 254)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone2') }}</span>
                  <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                      color="rgba(235, 247, 225)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone3') }}</span>
                  <span style="color: darkgrey; padding-right: 15px;"><v-icon class="pb-1" dense
                      color="rgba(255, 243, 215)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone4') }}</span>
                  <span style="color: darkgrey; padding-right: 10px;"><v-icon class="pb-1" dense
                      color="rgba(255, 216, 219)">mdi-square-rounded</v-icon> {{ $t('components.SaturationCurves.zone5') }}</span>
                  <span><v-icon class="pb-1" dense color="primary" small
                      @click.stop="optimalZonesInfoDialog = true">mdi-information-outline</v-icon></span>
                </div>
              </v-col>
            </v-row>
            <v-row v-if="!showOptimalZonesMessage">
              <v-col :cols="channelCols" v-for="[key, color] in channelEntries" v-bind:key="`optimalZone_${key}`">
                <span :style="{ textDecoration: 'underline', textDecorationColor: color }">{{ key }}</span>
                <channel-curves-chart-multi :data="saturationForKey(key)"
                  :key="`optimalZoneChart_${key}_${selectedPeriodCollapse}`"
                  :titleString="`${$t('components.SaturationCurves.optimalZones')} [${key}]`"
                  :exportImageName="`${client.name} - Optimal Zones [${key}]`"
                  :x-name="saturationXAxisName"
                  :y-name="`${customKpiName} (${outputUnitName})`" icon="chart-bell-curve-cumulative"
                  :export="true" :colors="channelColors" :show-diagonal="true" :show-data-zoom="true"
                  :show-tooltips="true" :scale="true" :filter-mode="null" :hide-title="true"
                  :period="chartPeriod" :show-quarters="chartShowQuarters"
                  :hide-datapoints="!showSaturationDatapoints"
                  :show-legend="false" elevation="elevation-0" />
              </v-col>
            </v-row>
            <v-row v-if="showOptimalZonesMessage">
              <v-col cols="12">
                <div class="text-center grey--text text--darken-1 py-8">
                  {{ $t('components.SaturationCurves.noOptimalZonesData', { name: selectedGroupDisplayName }) }}
                </div>
              </v-col>
            </v-row>
          </div>
          <div
            v-if="bootedSaturationTabs.includes(2)"
            v-show="tabSaturation === 2">
            <v-row>
              <v-col cols="12">
                <div class="mt-8 mb-5 float-right mr-10">
                  <span><v-icon class="pb-1" dense color="primary" small
                      @click.stop="marginalRoasInfoDialog = true">mdi-information-outline</v-icon></span>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col :cols="channelCols" v-for="[key, color] in channelEntries" v-bind:key="`marginalRoas_${key}`">
                <span :style="{ textDecoration: 'underline', textDecorationColor: color }">{{ key }}</span>
                <channel-curves-chart-multi :data="marginalRoasForKey(key)"
                  :key="`marginalRoasChart_${key}_${selectedPeriodCollapse}`"
                  :titleString="`${$t('components.SaturationCurves.marginalRoas')} [${key}]`"
                  :exportImageName="`${client.name} - Marginal ROAS [${key}]`"
                  :x-name="saturationXAxisName"
                  :y-name="`${customKpiName} (${outputUnitName})`"
                  :secondary-y-name="$t('components.SaturationCurves.marginalRoas')"
                  :colors="marginalRoasColors(key)"
                  :line-styles="marginalRoasLineStyles(key)" icon="chart-bell-curve-cumulative"
                  :export="true" :show-diagonal="false" :show-data-zoom="true"
                  :show-tooltips="true" :scale="true" :filter-mode="null" :hide-title="true"
                  :period="chartPeriod" :show-quarters="chartShowQuarters"
                  :hide-datapoints="!showSaturationDatapoints"
                  :show-legend="false" elevation="elevation-0" />
              </v-col>
            </v-row>
          </div>
        </div>
      </v-container>
    </text-box>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import TextBox from '@/components/TextBox.vue'
import ChannelCurvesChartMulti from '@/components/simulationDetails/ChannelCurvesChartMulti.vue'
import { saturationCurves } from '@/mixins/saturationCurves.js'
import {
  SATURATION_GROUP_KEY,
  hasSaturationGroups,
  defaultSaturationGroup,
  effectiveSaturation,
  hasOptimalZones,
  hasMeaningfulOptimalZones,
  hasMarginalRoas
} from '@/utils/saturationSections.js'
import {
  NONE_COLLAPSE,
  collapseOptionsFor,
  collapseSaturationDatapoints,
  defaultCollapseOption,
  hasSaturationDatapoints
} from '@/utils/saturationDatapoints.js'

// Width of the datapoints selector beside the tabs, and the gap (ml-4) before it.
const DATAPOINTS_SELECT_WIDTH = 260
const DATAPOINTS_SELECT_GAP = 16

export default {
  mixins: [saturationCurves],
  props: {
    // Per-channel saturation object: { channel: { x[], y[], datapoints[], datapoints_dates[], regions[] } }.
    // The parent injects whichever datapoints it wants overlaid (historical for the model report,
    // plan spend for a simulation).
    saturationData: {
      type: Object,
      required: true
    },
    channelColors: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    customKpiName: {
      type: String,
      required: true
    },
    outputUnitName: {
      type: String,
      required: true
    },
    spendUnitName: {
      type: String,
      required: true
    },
    canExportCurves: {
      type: Boolean,
      default: false
    },
    // i18n key labelling the datapoints selector. The model report keeps the historical
    // wording; a simulation overrides it (the overlaid points are the plan's spend, not historical).
    datapointsLabelKey: {
      type: String,
      default: 'components.SaturationCurves.showHistoricalSpend'
    },
    // When true and the saturation data carries a first-level "Instant Marketing" sub-key,
    // a selector is shown in the title bar to pick the group (Model Report, plan details
    // and the plan comparator all enable it); flat data keeps the previous behavior.
    enableGroupSelector: {
      type: Boolean,
      default: false
    },
    // Narrow-column layout (e.g. the simulation comparator): lay the General View legend
    // out horizontally below the chart instead of vertically on the right, and render the
    // per-channel Optimal Zones / Marginal ROAS charts one per row. Other usages (Model
    // Report, plan details) keep the default two-up layout with a right-side legend.
    // Drops the header TextBox would otherwise draw, for a caller that titles the section itself —
    // the same prop the charts inside it carry, and for the same reason.
    hideTitle: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      tabSaturation: 0,
      // Tabs that have been opened at least once. Their panels stay mounted afterwards so
      // ECharts instances persist across tab switches (see the template comment).
      bootedSaturationTabs: [],
      // Currently selected first-level group (model report only; null when not grouped).
      selectedSaturationGroup: null,
      // The one control over the spend datapoints, for every tab: NONE_COLLAPSE draws none,
      // any other value draws them collapsed to that period (see utils/saturationDatapoints.js).
      selectedPeriodCollapse: NONE_COLLAPSE,
      // True when the selector does not fit beside the tabs and moves to a row of its own.
      stackDatapointsSelect: false,
      datapointsSelectResizeObserver: null,
      selectedSaturationSpendUnit: null,
      saturationAlternativeUnitValues: {},
      tempAlternativeUnitValues: {},
      alternativeUnitDialog: false,
      optimalZonesInfoDialog: false,
      marginalRoasInfoDialog: false
    }
  },
  computed: {
    isWeeklyModel () {
      return !!(this.client && this.client.reference_model && this.client.reference_model.config &&
        this.client.reference_model.config.saturation_granularity === 'W')
    },
    periodCollapseItems () {
      // Every option other than None and the model's own frequency averages several of the
      // model's periods into one point, and says so ("Monthly Average"): the curve's x axis is
      // spend per model period, so the collapsed point is an average and not a period total.
      const native = defaultCollapseOption(this.isWeeklyModel)
      return collapseOptionsFor(this.isWeeklyModel).map(value => {
        const key = (value === NONE_COLLAPSE || value === native) ? value : value + 'Avg'
        return { text: this.$t('components.SaturationCurves.' + key), value: value }
      })
    },
    showSaturationDatapoints () {
      return this.selectedPeriodCollapse !== NONE_COLLAPSE
    },
    // The selector is shown when there are datapoints to draw. Note this is NOT the condition
    // the old switch used (`regions`), which was unrelated to the points it turned on.
    showPeriodCollapse () {
      return hasSaturationDatapoints(this.effectiveSaturationData)
    },
    // The selected data with its datapoints collapsed to the selected period. Identical (same
    // object) to effectiveSaturationData for the two identity cases: no collapse selected, and
    // a collapse to the model's own granularity.
    collapsedSaturationData () {
      return collapseSaturationDatapoints(
        this.effectiveSaturationData,
        this.selectedPeriodCollapse,
        defaultCollapseOption(this.isWeeklyModel)
      )
    },
    // The collapse the datapoint tooltips must date-format by, in the chart's own vocabulary.
    // With no collapse the dates are still the model's own periods, so they date-format by it.
    chartPeriod () {
      const option = this.selectedPeriodCollapse === NONE_COLLAPSE
        ? defaultCollapseOption(this.isWeeklyModel)
        : this.selectedPeriodCollapse
      if (option === 'weekly') return 'week'
      if (option === 'yearly') return 'year'
      return 'month'
    },
    chartShowQuarters () {
      return this.selectedPeriodCollapse === 'quarterly'
    },
    datapointsSelectWidth () {
      return DATAPOINTS_SELECT_WIDTH
    },
    channelCols () {
      // One channel per row in compact (narrow-column) layout, two-up otherwise.
      return this.compact ? 12 : 6
    },
    sortedChannels () {
      return Object.entries(this.channelColors)
        .sort(([a], [b]) => a.localeCompare(b))
    },
    hasSaturationGroups () {
      return hasSaturationGroups(this.saturationData, this.enableGroupSelector)
    },
    // The model's custom component names, as configured in Settings > Custom names > Component
    // names: { rawNameInTheReport: alias }. The first-level keys of a grouped saturation object
    // are components, not channels, so this — and not the media dictionary the parents apply to
    // the channels inside each group — is what names them.
    componentPrettyNamesDict () {
      return this.client && this.client.pretty_names && this.client.pretty_names.components
        ? this.client.pretty_names.components
        : {}
    },
    saturationGroupItems () {
      // {text, value} group labels, "Instant Marketing" first. The value keeps the real data
      // key: it is what the saturation object is indexed by and what SATURATION_GROUP_KEY is
      // compared against, so only the label is translated.
      if (!this.hasSaturationGroups) return []
      const keys = Object.keys(this.saturationData)
      return keys.slice()
        .sort((a, b) => (a === SATURATION_GROUP_KEY ? -1 : b === SATURATION_GROUP_KEY ? 1 : 0))
        .map(key => ({ text: this.groupDisplayName(key), value: key }))
    },
    // The saturation object actually rendered: the selected group, or the flat data.
    effectiveSaturationData () {
      return effectiveSaturation(this.saturationData, this.hasSaturationGroups ? this.selectedSaturationGroup : null)
    },
    // Channel iteration source for the per-channel tabs. In flat mode it is identical to the
    // previous Object.entries(channelColors) (no behavior change for plans/comparators); in
    // grouped mode it iterates the selected group's channels.
    channelEntries () {
      if (this.hasSaturationGroups) {
        return Object.keys(this.effectiveSaturationData)
          .sort((a, b) => a.localeCompare(b))
          .map(k => [k, this.channelColors[k]])
      }
      return Object.entries(this.channelColors).sort(([a], [b]) => a.localeCompare(b))
    },
    adjustedSaturationData () {
      if (this.selectedSaturationSpendUnit !== 'MXN' || !this.saturationAlternativeUnitValues) {
        return this.collapsedSaturationData
      } else {
        const adjustedData = {}
        // Iterate through each channel in the saturation data
        for (const channelName in this.collapsedSaturationData) {
          const channelData = this.collapsedSaturationData[channelName]
          const multiplier = this.saturationAlternativeUnitValues[channelName]
          if (multiplier !== undefined && channelData) {
            adjustedData[channelName] = {
              x: channelData.x ? channelData.x.map(value => value * multiplier) : channelData.x,
              y: channelData.y,
              datapoints: channelData.datapoints ? channelData.datapoints.map(point => ({
                x: point.x * multiplier,
                y: point.y
              })) : channelData.datapoints,
              // Carried through: the datapoint tooltips are indexed against it, and rebuilding
              // the channel without it left them dateless in MXN mode.
              datapoints_dates: channelData.datapoints_dates,
              regions: channelData.regions ? channelData.regions.map(value => value * multiplier) : channelData.regions
            }
          } else {
            // If no multiplier found or invalid data, keep original
            adjustedData[channelName] = channelData
          }
        }
        return adjustedData
      }
    },
    showOptimalZones () {
      return hasOptimalZones(this.effectiveSaturationData)
    },
    // True when the currently selected data has at least one channel with meaningful optimal
    // zones (four strictly-increasing region boundaries). Used to decide whether the Optimal
    // Zones tab renders the per-channel charts or a "no data" message instead.
    effectiveHasOptimalZones () {
      return hasMeaningfulOptimalZones(this.effectiveSaturationData)
    },
    // Show the "no Optimal Zones data" message (instead of the charts) only for grouped data:
    // flat data keeps its previous behavior (the charts render with no zone shading).
    showOptimalZonesMessage () {
      return this.hasSaturationGroups && !this.effectiveHasOptimalZones
    },
    // Display name of the selected group, used to name it in the "no Optimal Zones data"
    // message. Same label the selector shows, so the message and the dropdown agree.
    selectedGroupDisplayName () {
      if (!this.selectedSaturationGroup) return ''
      return this.groupDisplayName(this.selectedSaturationGroup)
    },
    showMarginalRoas () {
      return hasMarginalRoas(this.effectiveSaturationData)
    },
    saturationSpendUnitOptions () {
      return ['MXN', this.spendUnitName]
    },
    currentSaturationSpendUnitName () {
      if (this.selectedSaturationSpendUnit === 'MXN') {
        return 'MXN'
      } else {
        return this.spendUnitName
      }
    },
    hasSaturationAlternativeValues () {
      return this.saturationAlternativeUnitValues && Object.keys(this.saturationAlternativeUnitValues).length > 0
    },
    saturationChartGranularity () {
      // Add null safety check
      if (!this.client || !this.client.reference_model || !this.client.reference_model.config) {
        return ''
      }
      if (this.client.reference_model.config.saturation_granularity === 'M') {
        return this.$t('components.SaturationCurves.monthly')
      } else if (this.client.reference_model.config.saturation_granularity === 'W') {
        return this.$t('components.SaturationCurves.weekly')
      }
      return ''
    },
    saturationXAxisName () {
      const spend = this.$t('components.SaturationCurves.spend')
      const granularity = this.saturationChartGranularity
      const unit = this.currentSaturationSpendUnitName
      if (!granularity) {
        return spend + ' (' + unit + ')'
      }
      // Spanish: "Gasto Mensual (USD)", English: "Monthly Spend (USD)"
      if (this.$i18n.locale === 'es' || this.$i18n.locale === 'pt') {
        return spend + ' ' + granularity + ' (' + unit + ')'
      }
      return granularity + ' ' + spend + ' (' + unit + ')'
    }
  },
  methods: {
    // The datapoints selector rides on the tab strip, but the section is also mounted in
    // narrow columns (the What-If puts two of them side by side) where the tabs and a 260px
    // select do not fit on one line. Measured rather than guessed from a breakpoint: what
    // decides it is how wide the tabs themselves are, which varies with the language, with
    // how many tabs the model shows, and with the column the section was dropped into.
    updateDatapointsSelectLayout () {
      const row = this.$refs.saturationTabsRow
      const tabs = this.$refs.saturationTabs
      if (!row || !tabs || !tabs.$el) return
      const available = row.clientWidth
      // Zero while the section sits in a hidden panel; keep the last decision until it shows.
      if (!available) return
      // Sum the tabs themselves: v-tabs' own element stretches to fill the row, so its width
      // says nothing. Tabs hidden with v-show measure 0, which is what we want.
      const tabEls = tabs.$el.querySelectorAll('.v-tab')
      let tabsWidth = 0
      for (let i = 0; i < tabEls.length; i++) {
        tabsWidth += tabEls[i].offsetWidth
      }
      this.stackDatapointsSelect = tabsWidth + DATAPOINTS_SELECT_WIDTH + DATAPOINTS_SELECT_GAP > available
    },
    ...mapActions('clients', [
      'saveSaturationAlternativeValues'
    ]),
    // Label for a saturation group key: the component's custom name, or the raw key when it has
    // none. "Instant Marketing" is the exception — it is always shown as "Marketing", even when
    // a custom name is configured for it. That is deliberate: the PM team asked for that wording
    // explicitly during the "Mejoras App" project, so the custom name does NOT override it here.
    groupDisplayName (key) {
      if (key === SATURATION_GROUP_KEY) return 'Marketing'
      return this.componentPrettyNamesDict[key] || key
    },
    saturationForKey (key) {
      let saturation = {}
      saturation[key] = this.adjustedSaturationData[key]
      return saturation
    },
    marginalSeriesName (key) {
      return `${key} (mROAS)`
    },
    marginalRoasForKey (key) {
      const channel = this.adjustedSaturationData[key]
      const result = {}
      // Saturation curve (primary y-axis) — keep datapoints, drop zone regions
      result[key] = {
        x: channel.x,
        y: channel.y,
        datapoints: channel.datapoints,
        datapoints_dates: channel.datapoints_dates
      }
      // Marginal curve (secondary y-axis) — derivative of the saturation curve.
      // mROAS[i] = (y[i+1] - y[i]) / (x[i+1] - x[i]); it has one fewer element than x/y
      // and is plotted at the current investment level x[i+1].
      const marginalX = []
      const marginalY = []
      for (let i = 0; i < channel.x.length - 1; i++) {
        const dx = channel.x[i + 1] - channel.x[i]
        if (dx !== 0) {
          marginalX.push(channel.x[i + 1])
          marginalY.push((channel.y[i + 1] - channel.y[i]) / dx)
        }
      }
      result[this.marginalSeriesName(key)] = { x: marginalX, y: marginalY }
      return result
    },
    marginalRoasColors (key) {
      const colors = {}
      colors[key] = this.channelColors[key]
      colors[this.marginalSeriesName(key)] = '#E5733F'
      return colors
    },
    marginalRoasLineStyles (key) {
      const styles = {}
      styles[key] = { yAxisIndex: 0 }
      styles[this.marginalSeriesName(key)] = { yAxisIndex: 1, type: 'dashed', smooth: true }
      return JSON.stringify(styles)
    },
    filterNumericInput (value, channel) {
      // Remove commas and keep only dots for decimal separator
      const filteredValue = value.replace(/,/g, '')
      this.$set(this.tempAlternativeUnitValues, channel, filteredValue)
    },
    saveAlternativeUnits () {
      this.alternativeUnitDialog = false
      // Save the values from temp to actual
      this.saturationAlternativeUnitValues = { ...this.tempAlternativeUnitValues }
      this.saveSaturationAlternativeValues(this.saturationAlternativeUnitValues)
    },
    openEditCpiModal () {
      // Initialize temp values with existing values or empty
      this.tempAlternativeUnitValues = { ...this.saturationAlternativeUnitValues }
      // Initialize with 1 for any missing channels
      Object.keys(this.client.channels).forEach(channel => {
        if (!this.tempAlternativeUnitValues[channel]) {
          this.$set(this.tempAlternativeUnitValues, channel, 1)
        }
      })
      this.alternativeUnitDialog = true
    }
  },
  mounted () {
    this.$nextTick(this.updateDatapointsSelectLayout)
    if (typeof ResizeObserver !== 'undefined') {
      this.datapointsSelectResizeObserver = new ResizeObserver(this.updateDatapointsSelectLayout)
      if (this.$refs.saturationTabsRow) {
        this.datapointsSelectResizeObserver.observe(this.$refs.saturationTabsRow)
      }
    } else {
      window.addEventListener('resize', this.updateDatapointsSelectLayout)
    }
  },
  beforeDestroy () {
    if (this.datapointsSelectResizeObserver) {
      this.datapointsSelectResizeObserver.disconnect()
      this.datapointsSelectResizeObserver = null
    } else {
      window.removeEventListener('resize', this.updateDatapointsSelectLayout)
    }
  },
  watch: {
    // The tab strip's width changes with the language and with which tabs the model shows.
    '$i18n.locale': function () {
      this.$nextTick(this.updateDatapointsSelectLayout)
    },
    showMarginalRoas: function () {
      this.$nextTick(this.updateDatapointsSelectLayout)
    },
    showOptimalZones: function () {
      this.$nextTick(this.updateDatapointsSelectLayout)
    },
    hasSaturationGroups: {
      immediate: true,
      handler: function (grouped) {
        if (!grouped) {
          this.selectedSaturationGroup = null
          return
        }
        const items = this.saturationGroupItems
        if (this.selectedSaturationGroup && items.some(i => i.value === this.selectedSaturationGroup)) return
        this.selectedSaturationGroup = defaultSaturationGroup(this.saturationData, this.enableGroupSelector)
      }
    },
    // Drop a selection the model no longer offers (Weekly only exists for weekly models),
    // falling back to drawing no datapoints, which is where the section starts.
    isWeeklyModel: {
      immediate: true,
      handler: function (weekly) {
        if (collapseOptionsFor(weekly).indexOf(this.selectedPeriodCollapse) !== -1) return
        this.selectedPeriodCollapse = NONE_COLLAPSE
      }
    },
    tabSaturation: {
      immediate: true,
      handler: function (val) {
        // Mark a tab as booted the first time it becomes active so its panel mounts once
        // and then persists (kept hidden via v-show rather than destroyed).
        if (this.bootedSaturationTabs.indexOf(val) === -1) {
          this.bootedSaturationTabs.push(val)
        }
      }
    },
    client: {
      immediate: true,
      handler: function (newVal) {
        if (newVal && newVal.saturationAlternativeUnitValues) {
          this.saturationAlternativeUnitValues = newVal.saturationAlternativeUnitValues
        }
        // Default the spend unit selector to the model's spend unit once available.
        if (newVal && !this.selectedSaturationSpendUnit && this.spendUnitName) {
          this.selectedSaturationSpendUnit = this.spendUnitName
        }
        // Impressions models hide the General View tab and show Optimal Zones first.
        if (newVal && newVal.enableImpressionsModel) {
          this.tabSaturation = 1
        }
      }
    },
    selectedSaturationSpendUnit (newVal) {
      if (this.client && this.client.enableImpressionsModel && newVal === 'MXN' && !this.hasSaturationAlternativeValues) {
        // Only show modal automatically if no values exist yet
        // Initialize temp values with existing values or empty
        this.tempAlternativeUnitValues = { ...this.saturationAlternativeUnitValues }
        // Initialize with 1 for any missing channels
        Object.keys(this.client.channels).forEach(channel => {
          if (!this.tempAlternativeUnitValues[channel]) {
            this.$set(this.tempAlternativeUnitValues, channel, 1)
          }
        })
        this.alternativeUnitDialog = true
      }
    }
  },
  components: {
    TextBox,
    ChannelCurvesChartMulti
  }
}
</script>

<style scoped>
.channel-input >>> input[type="number"]::-webkit-outer-spin-button,
.channel-input >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.channel-input >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Additional Vuetify-specific targeting */
.channel-input >>> .v-input__control input[type="number"]::-webkit-outer-spin-button,
.channel-input >>> .v-input__control input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Keep the datapoints selector on the tab strip's baseline */
.datapoints-period-select.v-text-field {
  padding-top: 0;
  margin-top: 0;
  align-self: center;
}

/* Fix ECharts toolbox tooltip being clipped inside v-tabs */
.text-box-saturation >>> .v-tabs-items,
.text-box-saturation >>> .v-tab-item {
  overflow: visible;
}

/* Shrink the group select so it fits the section title bar */
.saturation-group-select.v-text-field {
  padding-top: 0;
  margin-top: 0;
}
.saturation-group-select >>> .v-input__control {
  min-height: 24px !important;
}
.saturation-group-select >>> .v-input__slot {
  min-height: 24px !important;
  padding: 0 8px;
}
.saturation-group-select >>> .v-select__selections {
  min-height: 24px;
  line-height: 20px;
  justify-content: flex-end;
  text-align: right;
}
.saturation-group-select >>> .v-select__selections input {
  height: 20px;
  width: 0;
  min-width: 0;
  flex: 0 0 0px;
  padding: 0;
}
.saturation-group-select >>> .v-input__append-inner {
  margin-top: 0;
  align-self: center;
}
</style>
